import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Dialog,
  DialogContent,
  Button,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { SiteAnalytics, PageAnalytics } from '../../../types/analytics';
import { LandingPage } from '../../../types';
import { PagePreview } from '../pageEditor/PagePreview';

export interface AnalyticsPageTableProps {
  analyticsData: SiteAnalytics | null;
  comparisonAnalyticsData: SiteAnalytics | null;
  currentSite: LandingPage | undefined;
  pageSortField: string;
  pageSortDirection: 'asc' | 'desc';
  onPageSort: (field: string) => void;
}

export const AnalyticsPageTable: React.FC<AnalyticsPageTableProps> = ({
  analyticsData,
  comparisonAnalyticsData,
  currentSite,
  pageSortField,
  pageSortDirection,
  onPageSort,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedPageForPreview, setSelectedPageForPreview] = useState<PageAnalytics | null>(null);

  // 並び替え済みページデータを取得
  const sortedPageData = useMemo(() => {
    if (!analyticsData?.pageData) return [];

    const sorted = [...analyticsData.pageData];

    if (pageSortField) {
      sorted.sort((a, b) => {
        let valueA: any;
        let valueB: any;

        switch (pageSortField) {
          case 'page':
            valueA = a.pageTitle || a.pageId;
            valueB = b.pageTitle || b.pageId;
            return pageSortDirection === 'asc'
              ? valueA.localeCompare(valueB, 'ja')
              : valueB.localeCompare(valueA, 'ja');
          case 'pv':
            valueA = analyticsData?.metrics.pageViews || 0;
            valueB = analyticsData?.metrics.pageViews || 0;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'arrivals':
            valueA = a.pageViews;
            valueB = b.pageViews;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'reachRate':
            valueA = a.stepReachRate;
            valueB = b.stepReachRate;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'exits':
            valueA = a.exitCount;
            valueB = b.exitCount;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'exitRate':
            valueA = a.exitRate;
            valueB = b.exitRate;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'avgDuration':
            valueA = a.averageSessionDuration;
            valueB = b.averageSessionDuration;
            return pageSortDirection === 'asc' ? valueA - valueB : valueB - valueA;
          case 'ctaClickRate':
            const rateA = a.pageViews > 0 ? (a.ctaClicks / a.pageViews) * 100 : 0;
            const rateB = b.pageViews > 0 ? (b.ctaClicks / b.pageViews) * 100 : 0;
            return pageSortDirection === 'asc' ? rateA - rateB : rateB - rateA;
          default:
            return 0;
        }
      });
    }

    return sorted;
  }, [analyticsData, pageSortField, pageSortDirection]);

  return (
    <>
      <Box sx={{ mb: 3, px: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontSize: '18px', fontWeight: 600, color: '#000000' }}>
          ページ別
        </Typography>
        <Paper sx={{ borderRadius: '24px', overflow: 'hidden' }}>
          <TableContainer
            sx={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '3px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                },
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FFFFFF' }}>
                  <TableCell align="center" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', pl: 3, pr: 2, width: 100, minWidth: 100 }}>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    ページ
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('pv')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      PV
                      {pageSortField === 'pv' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('arrivals')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      到達数
                      {pageSortField === 'arrivals' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 180,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('reachRate')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      ページ到達率
                      {pageSortField === 'reachRate' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('exits')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      離脱数
                      {pageSortField === 'exits' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 120,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('exitRate')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      離脱率
                      {pageSortField === 'exitRate' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 140,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('avgDuration')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      平均滞在時間
                      {pageSortField === 'avgDuration' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 140,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                    onClick={() => onPageSort('ctaClickRate')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      CTAクリック率
                      {pageSortField === 'ctaClickRate' && (
                        pageSortDirection === 'asc' ? <KeyboardArrowUpIcon sx={{ fontSize: 14 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedPageData.map((page) => {
                  const comparisonPage = comparisonAnalyticsData?.pageData.find(p => p.pageId === page.pageId);

                  // ページIDを「行-列」形式に変換（ページのインデックスから計算）
                  const pageIndex = currentSite?.pages?.findIndex(p => p.pageId === page.pageId) ?? -1;
                  const maxColumns = currentSite?.maxColumns || 1;
                  const row = pageIndex >= 0 ? Math.floor(pageIndex / maxColumns) : -1;
                  const column = pageIndex >= 0 ? pageIndex % maxColumns : -1;
                  const pageDisplayId = row >= 0 && column >= 0
                    ? `${row + 1}-${String.fromCharCode(65 + column)}`
                    : page.pageTitle || page.pageId;

                  // 比較期間との差分を計算
                  const pvDiff = page.pageViews - (comparisonPage?.pageViews || 0);
                  const arrivalsDiff = page.uniqueUsers - (comparisonPage?.uniqueUsers || 0);
                  const reachRateDiff = page.stepReachRate - (comparisonPage?.stepReachRate || 0);
                  const exitsDiff = page.exitCount - (comparisonPage?.exitCount || 0);
                  const exitRateDiff = page.exitRate - (comparisonPage?.exitRate || 0);
                  const avgDurationDiff = page.averageSessionDuration - (comparisonPage?.averageSessionDuration || 0);
                  const currentCtaClickRate = page.pageViews > 0 ? (page.ctaClicks / page.pageViews) * 100 : 0;
                  const comparisonCtaClickRate = (comparisonPage && comparisonPage.pageViews > 0) ? (comparisonPage.ctaClicks / comparisonPage.pageViews) * 100 : 0;
                  const ctaClickRateDiff = currentCtaClickRate - comparisonCtaClickRate;

                  // プログレスバーの色を決定（到達率に応じて）
                  const getProgressBarColor = (rate: number) => {
                    if (rate > 80) return '#1B76D2';
                    if (rate > 60) return '#59C1BA';
                    if (rate > 40) return '#F4DC21';
                    if (rate > 20) return '#F49121';
                    return '#D94557';
                  };

                  const progressColor = getProgressBarColor(page.stepReachRate);

                  return (
                    <TableRow
                      key={page.pageId}
                      hover
                      sx={{
                        backgroundColor: 'white',
                        '&:hover': {
                          backgroundColor: '#F5F5F5',
                        },
                      }}
                    >
                      <TableCell align="center" sx={{ borderBottom: 'none', py: 1.5, pl: 3, pr: 2, width: 100, minWidth: 100 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          {page.backgroundImage ? (
                            <Box
                              component="img"
                              src={page.backgroundImage}
                              alt={pageDisplayId}
                              onClick={() => {
                                setSelectedPageForPreview(page);
                                setPreviewModalOpen(true);
                              }}
                              sx={{
                                width: 'auto',
                                height: '50px',
                                maxWidth: '70px',
                                display: 'block',
                                objectFit: 'contain',
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                              }}
                            />
                          ) : (
                            <Box
                              onClick={() => {
                                setSelectedPageForPreview(page);
                                setPreviewModalOpen(true);
                              }}
                              sx={{
                                width: '70px',
                                height: '50px',
                                backgroundColor: '#F5F5F5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#E0E0E0',
                                },
                              }}
                            >
                              <Typography sx={{ color: '#9E9E9E', fontSize: '10px' }}>No Image</Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                          {pageDisplayId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {page.pageViews.toLocaleString()}
                          </Typography>
                          {(() => {
                            if (Math.abs(pvDiff) < 0.01) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (pvDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{pvDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{pvDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {page.pageViews.toLocaleString()}
                          </Typography>
                          {(() => {
                            if (arrivalsDiff === 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (arrivalsDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{arrivalsDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{arrivalsDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2} width="100%">
                          <Box flex={1} position="relative">
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(page.stepReachRate, 100)}
                              sx={{
                                height: 16,
                                borderRadius: '4px',
                                backgroundColor: '#E0E0E0',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: progressColor,
                                  borderRadius: '4px',
                                },
                              }}
                            />
                            {page.stepReachRate === 0 && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  right: -12,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Box
                                  component="img"
                                  src="/assets/images/alart.svg"
                                  alt="alert"
                                  sx={{
                                    width: 24,
                                    height: 24,
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} minWidth={60}>
                            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25, minWidth: 45 }}>
                              {page.stepReachRate.toFixed(2)}%
                            </Typography>
                            {(() => {
                              if (Math.abs(reachRateDiff) < 0.01) {
                                return (
                                  <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                    <span style={{ color: '#9E9E9E' }}>(</span>
                                    <span style={{ color: '#9E9E9E' }}>±0%</span>
                                    <span style={{ color: '#9E9E9E' }}>)</span>
                                  </Typography>
                                );
                              } else if (reachRateDiff > 0) {
                                return (
                                  <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                    <span style={{ color: '#9E9E9E' }}>(</span>
                                    <span style={{ color: '#FF9800' }}>↑+{reachRateDiff.toFixed(2)}%</span>
                                    <span style={{ color: '#9E9E9E' }}>)</span>
                                  </Typography>
                                );
                              } else {
                                return (
                                  <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                    <span style={{ color: '#9E9E9E' }}>(</span>
                                    <span style={{ color: '#1B76D2' }}>↓{reachRateDiff.toFixed(2)}%</span>
                                    <span style={{ color: '#9E9E9E' }}>)</span>
                                  </Typography>
                                );
                              }
                            })()}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {page.exitCount.toLocaleString()}
                          </Typography>
                          {(() => {
                            if (exitsDiff === 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (exitsDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{exitsDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{exitsDiff.toLocaleString()}</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {page.exitRate.toFixed(2)}%
                          </Typography>
                          {(() => {
                            if (Math.abs(exitRateDiff) < 0.01) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (exitRateDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{exitRateDiff.toFixed(2)}%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{exitRateDiff.toFixed(2)}%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {page.averageSessionDuration.toFixed(2)}秒
                          </Typography>
                          {(() => {
                            if (Math.abs(avgDurationDiff) < 0.01) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0秒</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (avgDurationDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{avgDurationDiff.toFixed(2)}秒</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{avgDurationDiff.toFixed(2)}秒</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="center">
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#000000', lineHeight: 1.2, mb: -0.25 }}>
                            {currentCtaClickRate.toFixed(2)}%
                          </Typography>
                          {(() => {
                            if (Math.abs(ctaClickRateDiff) < 0.01) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#9E9E9E' }}>±0%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else if (ctaClickRateDiff > 0) {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#FF9800' }}>↑+{ctaClickRateDiff.toFixed(2)}%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            } else {
                              return (
                                <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                                  <span style={{ color: '#9E9E9E' }}>(</span>
                                  <span style={{ color: '#1B76D2' }}>↓{ctaClickRateDiff.toFixed(2)}%</span>
                                  <span style={{ color: '#9E9E9E' }}>)</span>
                                </Typography>
                              );
                            }
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* ページプレビューモーダル */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: '100%',
            width: '100%',
            height: '100%',
            margin: 0,
            borderRadius: 0,
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogContent sx={{ p: 0, overflow: 'hidden', height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {selectedPageForPreview && currentSite && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              {/* メインモーダル: プレビューと配置（枠線なし、背景色なし） */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                {/* 左側: プレビュー */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {(() => {
                    // PageAnalyticsからPage型を構築
                    const pageForPreview = currentSite.pages?.find(
                      (p) => (p.id || p.pageId) === selectedPageForPreview.pageId
                    ) || null;

                    if (pageForPreview) {
                      return (
                        <PagePreview
                          page={pageForPreview}
                          codeContent={pageForPreview.customHtml || ''}
                          previewType="settings"
                          ctaButtonVisible={pageForPreview.ctaButtonVisible !== false}
                          ctaButtonIds={pageForPreview.ctaButtonIds || []}
                          companyInfoVisible={pageForPreview.companyInfoVisible !== false}
                        />
                      );
                    }
                    return (
                      <Box
                        sx={{
                          width: '282px',
                          height: '468px',
                          backgroundColor: '#FFFFFF',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ color: '#9E9E9E' }}>プレビューを表示できません</Typography>
                      </Box>
                    );
                  })()}
                </Box>

                {/* 右側: 配置表示 */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {(() => {
                    const targetPage = currentSite.pages?.find(
                      (p) => (p.id || p.pageId) === selectedPageForPreview.pageId
                    );

                    if (!targetPage) {
                      return null;
                    }

                    // 行と列の最大値を取得
                    const maxRow = Math.max(...(currentSite.pages?.map((p) => p.row) || [1]));
                    const maxColumn = currentSite.pages?.reduce((max, p) => {
                      const colNum = p.column.length === 1
                        ? p.column.charCodeAt(0) - 'A'.charCodeAt(0) + 1
                        : 26 + (p.column.charCodeAt(0) - 'A'.charCodeAt(0) + 1) * 26 + (p.column.charCodeAt(1) - 'A'.charCodeAt(0) + 1);
                      return Math.max(max, colNum);
                    }, 1) || 1;

                    return (
                      <Box>
                        {/* ページ番号表示 */}
                        <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', mb: 2, textAlign: 'center' }}>
                          {targetPage.row}-{targetPage.column}
                        </Typography>
                        {/* グリッド表示 */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${maxColumn}, 1fr)`,
                            gap: 1,
                            maxWidth: '400px',
                          }}
                        >
                          {Array.from({ length: maxRow }, (_, rowIndex) => {
                            const row = rowIndex + 1;
                            return Array.from({ length: maxColumn }, (_, colIndex) => {
                              const column = colIndex < 26
                                ? String.fromCharCode('A'.charCodeAt(0) + colIndex)
                                : String.fromCharCode('A'.charCodeAt(0) + Math.floor(colIndex / 26) - 1) + String.fromCharCode('A'.charCodeAt(0) + (colIndex % 26));
                              const pageAtPosition = currentSite.pages?.find(
                                (p) => p.row === row && p.column === column
                              );
                              const isTargetPage = targetPage.row === row && targetPage.column === column;

                              return (
                                <Box
                                  key={`${row}-${column}`}
                                  sx={{
                                    aspectRatio: '1',
                                    backgroundColor: isTargetPage ? '#FFFFFF' : 'transparent',
                                    border: '1px solid #FFFFFF',
                                    borderRadius: '4px',
                                    minHeight: '40px',
                                  }}
                                />
                              );
                            });
                          })}
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </Box>

              {/* 閉じるボタン（メインモーダルの外） */}
              <Button
                onClick={() => setPreviewModalOpen(false)}
                variant="contained"
                sx={{
                  backgroundColor: '#666666',
                  color: '#FFFFFF',
                  padding: '12px 32px',
                  fontSize: '16px',
                  borderRadius: '12px',
                  minWidth: '120px',
                  '&:hover': {
                    backgroundColor: '#555555',
                  },
                }}
              >
                閉じる
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

